// App.js — PST (Precision Scrubber Tool)
// Revamped, modern protocol picker inline (no new files). Universe table stays full-bleed.
// Logic flow preserved from your last version.

import React, { useState, useEffect } from 'react';
import ODAGTableSelector from './components/ODAGTableSelector';
import CPETableSelector from './components/CPETableSelector';
import SNPCCTableSelector from './components/SNPCCTableSelector';
import CDAGTableSelector from './components/CDAGTableSelector';
import FATableSelector from './components/FATableSelector';
import UniversePeriodModal from './components/UniversePeriodModal';
import SubmitFinalDialog from './components/SubmitFinalDialog';
import FileUploadPanel from './components/FileUploadPanel';
import CPEFileUploadPanel from './components/CPEFileUploadPanel';
import SNPCCFileUploadPanel from './components/SNPCCFileUploadPanel';
import UniverseViewer from './components/UniverseViewer';
import SummaryCard from './components/SummaryCard';
import SubmissionsViewer from './components/SubmissionsViewer';
import ScrubberSidebar, { PlatformRail } from './components/ScrubberSidebar';
import PendingUniverseRequestBanner from './components/PendingUniverseRequestBanner';
import HRATIAViewer from './components/HRATIAViewer';
import FDRPortal from './components/fdr/FDRPortal';
import FDRInvitationsList from './components/fdr/FDRInvitationsList';
import FDRSubmissionsViewer from './components/fdr/FDRSubmissionsViewer';
import AggregatorPanel from './components/aggregator/AggregatorPanel';
import { CalendarIcon } from '@heroicons/react/24/outline';
import {
  TableCellsIcon,
  DocumentCheckIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  BeakerIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { SessionProvider, useSession, AccessControl } from './components/SessionManager';
import { SCRUBBER_API_BASE } from './api/config';
import { csrfFetch } from './api/csrf';

const PROTOCOLS = [
  { key: 'ODAG',  label: 'ODAG',  desc: 'Part C Organization Determinations, Appeals, and Grievances (ODAG)', icon: TableCellsIcon, color: 'from-violet-500 to-purple-600' },
  { key: 'CDAG',  label: 'CDAG',  desc: 'Part D Coverage Determinations, Appeals, and Grievances (CDAG)', icon: DocumentCheckIcon, color: 'from-orange-500 to-amber-600' },
  { key: 'FA',    label: 'FA',    desc: 'Part D Formulary and Benefit Administration (FA)', icon: BeakerIcon, color: 'from-pink-500 to-rose-600' },
  { key: 'SNPCC', label: 'SNPCC', desc: 'Special Needs Plans Care Coordination (SNPCC)', icon: ClipboardDocumentCheckIcon, color: 'from-emerald-500 to-teal-600' },
  { key: 'CPE',   label: 'CPE',   desc: 'Medicare Part C and Part D Compliance Program Effectiveness (CPE)', icon: ShieldCheckIcon, color: 'from-amber-500 to-orange-600' },
];

// Ultra-modern protocol picker with light glassmorphism
function ProtocolPicker({ onSelect, userScopes = [] }) {
  // Show all protocols for now (remove access control filtering temporarily)
  const accessibleProtocols = PROTOCOLS;

  if (accessibleProtocols.length === 0) {
    return (
      <div className="text-center p-12 bg-white rounded-2xl border border-red-200">
        <h3 className="text-lg font-bold text-red-600 mb-2">No Protocol Access</h3>
        <p className="text-slate-500 text-sm">You don't have access to any protocols. Contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {accessibleProtocols.map(({ key, label, desc, icon: Icon, color }) => (
        <button
          key={key}
          onClick={() => onSelect(label)}
          className="group relative w-full text-left bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-500/30 overflow-hidden"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${color} shadow-sm flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-slate-800 group-hover:text-rose-700 transition-colors">{label}</h3>
                <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-semibold rounded-full border border-slate-200 uppercase tracking-wide">CMS</span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <span className="text-rose-600 font-semibold text-sm group-hover:text-rose-700 transition-colors">Select Protocol →</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function AppContent() {
  const { user } = useSession();

  // Embedded mode: when Scrubber is loaded inside Core's iframe shell, our
  // PlatformRail + ScrubberSidebar + top bar would duplicate Core's chrome.
  // Detect either explicit ?embedded=1 OR being framed and suppress our own
  // chrome so the experience feels like a single product.
  const isEmbedded = typeof window !== 'undefined' && (
    new URLSearchParams(window.location.search).get('embedded') === '1' ||
    window.self !== window.top
  );

  const [selectedProtocol, setSelectedProtocol] = useState(null);
  // WHOSE universe is being submitted — 'Internal' or an FDR picked from
  // the FDR Oversight module's live list. NEVER typed: a typo would silently
  // break the match with the Precision COA row the submission scores, so the
  // only names offered are the module's own.
  const [entityLabel, setEntityLabel] = useState('');
  const [entityOptions, setEntityOptions] = useState([]);
  useEffect(() => {
    fetch(`${SCRUBBER_API_BASE}/api/fdr-entities`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setEntityOptions((d && d.entities) || []))
      .catch(() => setEntityOptions([]));
  }, []);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [universePeriod, setUniversePeriod] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [headerCorrections, setHeaderCorrections] = useState({});
  const [activeView, setActiveView] = useState('scrubber'); // Current active view from sidebar
  const [myStatus, setMyStatus] = useState(null); // latest submission status for current protocol/period
  const [engagementDate, setEngagementDate] = useState(null); // For SNPCC

  // SNPCC universes are "as of" the engagement date and carry no start/end
  // upload step of their own. The approved record still needs a declared
  // universe period — it is what the COA sync picker matches months against —
  // so declare the CMS testing window: 12 calendar months back, ending at the
  // engagement date (live-found 2026-08-25: SNPE submissions read
  // "No period declared at upload" in the picker).
  const snpccDeclaredPeriod = () => {
    const d = String(engagementDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    const [y, m, day] = d.split('-').map(Number);
    const s = new Date(Date.UTC(y - 1, m - 1, day));
    if (isNaN(s.getTime())) return null;
    return { startDate: s.toISOString().slice(0, 10), endDate: d };
  };
  const [hratIAPreview, setHratIAPreview] = useState(null);
  const [isGeneratingHratIA, setIsGeneratingHratIA] = useState(false);
  
  // User data comes from Core session via useSession hook

  // Always-visible actions in the top bar when viewing results
  const handleExport = async (type) => {
    try {
      // Table 2IA is a CMS submission table, not a review workbook: it exports
      // the generated impact analysis rows on their own sheet. (This used to
      // post format:"hrat-ia" to /api/export, which answers 400 Unsupported
      // format and would have carried the original SNPE universe.)
      if (type === 'hrat-ia') {
        if (!hratIAPreview?.rows?.length) {
          alert('Generate the impact analysis table first.');
          return;
        }
        const respIA = await csrfFetch(`${SCRUBBER_API_BASE}/api/snpcc/export-hrat-ia`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: hratIAPreview.rows })
        });
        if (!respIA.ok) {
          const text = await respIA.text();
          throw new Error(text || `Export failed with ${respIA.status}`);
        }
        const blobIA = await respIA.blob();
        const urlIA = window.URL.createObjectURL(blobIA);
        const aIA = document.createElement('a');
        aIA.href = urlIA;
        aIA.download = 'HRAT-IA_Table_2IA.xlsx';
        document.body.appendChild(aIA);
        aIA.click();
        aIA.remove();
        window.URL.revokeObjectURL(urlIA);
        return;
      }
      if (!validationResult?.universe) return;
      // A universe past the review cap: the browser only holds the rows that
      // need review, so the complete export is built server side from the
      // original file, inside a worker, with nothing stored anywhere.
      if (type === 'xlsx' && validationResult?.universeMode === 'issues-only' && uploadedFile) {
        const fd = new FormData();
        fd.append('file', uploadedFile);
        fd.append('universeType', selectedTable?.universeType || '');
        fd.append('tableLabel', selectedTable?.label || 'Unknown Table');
        if (universePeriod) fd.append('universePeriod', JSON.stringify(universePeriod));
        // SNPE evaluators anchor on the engagement letter date the review used.
        if (selectedProtocol === 'SNPCC' && engagementDate) fd.append('engagementLetterDate', String(engagementDate).slice(0, 10));
        const respFull = await csrfFetch(`${SCRUBBER_API_BASE}/api/export/full`, { method: 'POST', body: fd });
        if (!respFull.ok) {
          const text = await respFull.text();
          throw new Error(text || `Export failed with ${respFull.status}`);
        }
        const blobF = await respFull.blob();
        const urlF = window.URL.createObjectURL(blobF);
        const aF = document.createElement('a');
        aF.href = urlF;
        aF.download = `${selectedTable?.label?.replace(/\s+/g, '_') || 'universe'}_complete_export.xlsx`;
        document.body.appendChild(aF);
        aF.click();
        aF.remove();
        window.URL.revokeObjectURL(urlF);
        return;
      }
      // The export carries the COMPLETE universe the reviewer is looking at,
      // with every error highlighted on its cell and listed on its row.
      // Nothing is stored anywhere: the rows travel for this one response
      // and come back as the file.
      const resp = await csrfFetch(`${SCRUBBER_API_BASE}/api/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: type,
          errors: validationResult?.errors || [],
          universe: validationResult?.universe || [],
          universeMode: validationResult?.universeMode || 'full',
          cleanRowsOmitted: validationResult?.universeCleanRowsOmitted || 0,
              timeliness: validationResult?.timeliness || null,
              universeRowCount: validationResult?.totalRows || validationResult?.stats?.totalRows || 0,
              errorCountTotal: validationResult?.totalErrorCount || 0,
          filename: selectedTable?.universeType || 'universe_export',
          tableLabel: selectedTable?.label || 'Unknown Table',
          timelinessSummary: validationResult?.timelinessSummary || null,
          monthlyTimeliness: validationResult?.monthlyTimeliness || null
        })
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Export failed with ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTable?.label?.replace(/\s+/g, '_') || 'universe'}_export.${type}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Export failed. Check console for details.');
    }
  };

  const handleGenerateHratIA = async () => {
    if (!validationResult?.universe) {
      alert('No universe data available for HRAT-IA generation');
      return;
    }

    setIsGeneratingHratIA(true);
    
    try {
      console.log('🛠 DEBUG — Generating HRAT-IA preview...');
      
      const response = await csrfFetch(`${SCRUBBER_API_BASE}/api/snpcc/generate-hrat-ia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          universe: validationResult.universe,
          engagementLetterDate: engagementDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HRAT-IA generation failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ HRAT-IA generated:', result);
      
      setHratIAPreview(result);
      
      // Show confirmation
      alert(`✅ HRAT-IA Preview Generated!\n\n${result.count} enrollees with timeliness issues identified.\n\nReview the preview below before final submission.`);
      
    } catch (error) {
      console.error('❌ HRAT-IA generation failed:', error);
      alert(`HRAT-IA generation failed: ${error.message}`);
    } finally {
      setIsGeneratingHratIA(false);
    }
  };

  const handleSubmitFinal = () => {
    // Attribution is a deliberate human statement, never a default that
    // happened to be right. No entity, no submission.
    if (!entityLabel) {
      alert('Select whose universe this is — Internal or the FDR — before submitting.');
      return;
    }
    // The destination — monitoring or an open audit — is a deliberate
    // choice made in the dialog, never a default.
    setShowSubmitDialog(true);
  };

  // The monitoring path: the existing compliance-review submission,
  // untouched. Called by the submit dialog when Monitoring is chosen.
  const submitMonitoringFinal = async () => {
    try {

      // Check if this is a resubmission
      const resubmissionContext = sessionStorage.getItem('resubmissionContext');
      let response;
      
      if (resubmissionContext) {
        const context = JSON.parse(resubmissionContext);
        if (context.mode === 'resubmit') {
          // Use resubmit endpoint to update existing submission
          response = await csrfFetch(`${SCRUBBER_API_BASE}/api/resubmit/${context.originalId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              universe: [],
              timeliness: validationResult?.timeliness || null,
              universeRowCount: validationResult?.totalRows || 0,
              errorCountTotal: validationResult?.totalErrorCount || 0,
              errors: validationResult?.errors || [],
              selectedTable: selectedTable || {},
              filename: selectedTable?.universeType || 'universe_export',
              universePeriod: context.period || universePeriod || (selectedProtocol === 'SNPCC' ? snpccDeclaredPeriod() : null),
              protocol: selectedProtocol || (selectedTable?.protocol || null),
              userEmail: getUserEmail(),
              entityLabel: entityLabel
            })
          });
        } else {
          // Regular new submission
          response = await csrfFetch(`${SCRUBBER_API_BASE}/api/submit-final`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              universe: [],
              timeliness: validationResult?.timeliness || null,
              universeRowCount: validationResult?.totalRows || 0,
              errorCountTotal: validationResult?.totalErrorCount || 0,
              errors: validationResult?.errors || [],
              selectedTable: selectedTable || {},
              filename: selectedTable?.universeType || 'universe_export',
              universePeriod: universePeriod || (selectedProtocol === 'SNPCC' ? snpccDeclaredPeriod() : null),
              protocol: selectedProtocol || (selectedTable?.protocol || null),
              userEmail: getUserEmail(),
              entityLabel: entityLabel
            })
          });
        }
      } else {
        // Regular new submission
        response = await csrfFetch(`${SCRUBBER_API_BASE}/api/submit-final`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            universe: [],
              timeliness: validationResult?.timeliness || null,
              universeRowCount: validationResult?.totalRows || 0,
              errorCountTotal: validationResult?.totalErrorCount || 0,
            errors: validationResult?.errors || [],
            selectedTable: selectedTable || {},
            filename: selectedTable?.universeType || 'universe_export',
            universePeriod: universePeriod || (selectedProtocol === 'SNPCC' ? snpccDeclaredPeriod() : null),
            protocol: selectedProtocol || (selectedTable?.protocol || null),
            userEmail: getUserEmail(),
            entityLabel: entityLabel
          })
        });
      }
      if (!response.ok) {
        throw new Error(`Submission failed: ${response.status}`);
      }
      const result = await response.json();
      
      // Clear resubmission context after successful submission
      if (resubmissionContext) {
        sessionStorage.removeItem('resubmissionContext');
        alert(`✅ Universe resubmitted successfully!\n\nSubmission ID: ${result.submissionId}\nStatus: ${result.status}\n\nThe existing submission has been updated with your corrections.`);
      } else {
        alert(`✅ Universe submitted successfully!\n\nSubmission ID: ${result.submissionId}\nStatus: Submitted for compliance review`);
      }
    } catch (error) {
      console.error('❌ Error submitting universe:', error);
      alert(`❌ Failed to submit universe: ${error.message}`);
    }
  };

  // --- Compliance Review (list) ---
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [reviewNotes, setReviewNotes] = useState({});
  const [reviewReason, setReviewReason] = useState({});
  
  // --- Review Period Filters ---
  const [reviewDateFrom, setReviewDateFrom] = useState('');
  const [reviewDateTo, setReviewDateTo] = useState('');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('all');
  const [reviewProtocolFilter, setReviewProtocolFilter] = useState('all');
  
  // --- Notifications ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState({});

  const allowedProtocols = (user?.app_roles?.scrubber?.scopes) || [];

  // Filter submissions based on review period and other criteria
  const getFilteredSubmissions = () => {
    let filtered = submissions;

    // Date range filter
    if (reviewDateFrom) {
      const fromDate = new Date(reviewDateFrom);
      filtered = filtered.filter(s => new Date(s.timestamp) >= fromDate);
    }
    if (reviewDateTo) {
      const toDate = new Date(reviewDateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire end date
      filtered = filtered.filter(s => new Date(s.timestamp) <= toDate);
    }

    // Status filter
    if (reviewStatusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === reviewStatusFilter);
    }

    // Protocol filter
    if (reviewProtocolFilter !== 'all') {
      filtered = filtered.filter(s => s.protocol === reviewProtocolFilter);
    }

    return filtered;
  };

  // Quick date range presets
  const setQuickDateRange = (range) => {
    const today = new Date();
    const from = new Date();
    
    switch (range) {
      case 'today':
        setReviewDateFrom(today.toISOString().split('T')[0]);
        setReviewDateTo(today.toISOString().split('T')[0]);
        break;
      case 'week':
        from.setDate(today.getDate() - 7);
        setReviewDateFrom(from.toISOString().split('T')[0]);
        setReviewDateTo(today.toISOString().split('T')[0]);
        break;
      case 'month':
        from.setMonth(today.getMonth() - 1);
        setReviewDateFrom(from.toISOString().split('T')[0]);
        setReviewDateTo(today.toISOString().split('T')[0]);
        break;
      case 'clear':
        setReviewDateFrom('');
        setReviewDateTo('');
        setReviewStatusFilter('all');
        setReviewProtocolFilter('all');
        break;
      default:
        break;
    }
  };

  // Export individual submission errors to Excel
  const handleIndividualExport = async (submission) => {
    try {
      if (!submission.errorDetails || submission.errorDetails.length === 0) {
        alert('No errors to export for this submission.');
        return;
      }

      const exportData = submission.errorDetails.map((error, index) => ({
        'Error #': index + 1,
        'Row Number': error.row,
        'Field Name': error.field,
        'Error Description': error.message,
        'Error Category': error.category,
        'Submission Info': `${submission.protocol} - ${submission.tableLabel}`,
        'Submitted By': submission.submittedBy,
        'Submission Date': new Date(submission.timestamp).toLocaleString(),
        'Status': submission.status.replace('_', ' '),
        'Universe Period': submission.period ? `${submission.period.startDate} to ${submission.period.endDate}` : 'N/A'
      }));

      // Convert to CSV format for Excel
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header]?.toString() || '';
            return value.includes(',') ? `"${value}"` : value;
          }).join(',')
        )
      ].join('\n');

      // Download as Excel file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const fileName = `${submission.protocol}_errors_${submission.id}_${new Date().toISOString().split('T')[0]}.csv`;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
      
    } catch (error) {
      console.error('Individual export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Export compliance review data to Excel
  const handleComplianceExport = async () => {
    try {
      const exportData = submissions.map(s => ({
        'Submission Date': new Date(s.timestamp).toLocaleString(),
        'Protocol': s.protocol,
        'Table': s.tableLabel,
        'Universe Period': s.period && s.period.startDate && s.period.endDate 
          ? `${s.period.startDate} to ${s.period.endDate}`
          : s.engagementDate 
            ? `Engagement: ${s.engagementDate}`
            : 'Not specified',
        'Submitted By': s.submittedBy,
        'Department': s.department || 'N/A',
        'Universe Rows': s.universeRowCount,
        'Error Count': s.errorCount,
        'Status': s.status.replace('_', ' '),
        'Reviewed By': s.reviewedBy || 'N/A',
        'Review Date': s.reviewedAt ? new Date(s.reviewedAt).toLocaleString() : 'N/A',
        'Compliance Notes': s.complianceNotes || 'N/A',
        'Version': s.version || 1,
        'Error Types': Object.entries(s.errorTypes || {})
          .filter(([_, count]) => count > 0)
          .map(([type, count]) => `${type}: ${count}`)
          .join('; ') || 'None'
      }));

      // Convert to CSV format for Excel
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header]?.toString() || '';
            return value.includes(',') ? `"${value}"` : value;
          }).join(',')
        )
      ].join('\n');

      // Download as Excel file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `compliance_review_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Helper function to get current user email
  const getUserEmail = () => {
    return user?.email || user?.name || 'unknown';
  };

  // Helper function to check if user is compliance admin
  const isComplianceAdmin = () => {
    console.log('🔍 User role check:', user?.role, user);
    console.log('🔍 App roles:', user?.app_roles?.scrubber);
    console.log('🔍 User email:', user?.email, user?.name);
    
    // If no user, assume department owner (safer default)
    if (!user) {
      console.log('❌ No user found, defaulting to department owner');
      return false;
    }
    
    // TYPE-FIRST (operator law 2026-08-24, extended here 2026-08-25):
    // the explicit user_type outranks everything else. A compliance officer
    // reviews and approves; an org access administrator never touches
    // compliance data. This is what keeps the review working after Core's
    // migration 315 removed legacy app-role rows from accounts whose stale
    // role column said 'superuser' while their real type is compliance.
    const userType = user?.effective_user_type || user?.user_type;
    if (userType === 'org_superadmin') {
      console.log('🎯 Type-first: org access admin -> never compliance admin');
      return false;
    }
    if (userType === 'compliance_officer' || userType === 'compliance_analyst') {
      console.log('🎯 Type-first: compliance department -> Compliance Admin');
      return true;
    }

    // PRIORITY 1: Check app-specific role first (most important)
    const scrubberRole = user?.app_roles?.scrubber?.role;
    if (scrubberRole) {
      const isScrubberAdmin = scrubberRole === 'admin' || scrubberRole === 'compliance_admin';
      console.log(`🎯 App-specific role decision: ${scrubberRole} -> ${isScrubberAdmin ? 'Compliance Admin' : 'Department Owner'}`);
      return isScrubberAdmin;
    }
    
    // PRIORITY 2: Fallback to system role only if no app role
    const isSystemAdmin = user?.role === 'superadmin' || user?.role === 'compliance_admin';
    console.log(`🎯 System role fallback: ${user?.role} -> ${isSystemAdmin ? 'Compliance Admin' : 'Department Owner'}`);
    
    return isSystemAdmin;
  };

  // Load notifications
  const loadNotifications = async () => {
    try {
      const role = isComplianceAdmin() ? 'compliance_admin' : 'department_owner';
      const res = await fetch(`${SCRUBBER_API_BASE}/api/notifications?role=${role}`);
      const data = await res.json();
      if (data?.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (e) {
      console.error('❌ Load notifications failed', e);
    }
  };

  const loadSubmissions = async () => {
    try {
      setLoadingSubs(true);
      const res = await fetch(`${SCRUBBER_API_BASE}/api/submissions`);
      const data = await res.json();
      if (data?.success) {
        // Filter by allowed protocols client-side
        let list = (data.submissions || []).filter(s => allowedProtocols.length === 0 || allowedProtocols.includes(s.protocol));
        
        // Hide approved submissions that have newer versions (completed workflow)
        if (!isComplianceAdmin()) {
          // For department owners, hide approved submissions if they have a newer submission for the same protocol/period
          list = list.filter(submission => {
            if (submission.status === 'approved') {
              // Check if there's a newer submission for the same protocol/period by the same user
              const hasNewerSubmission = list.some(other => 
                other.id !== submission.id &&
                other.submittedBy === submission.submittedBy &&
                other.protocol === submission.protocol &&
                other.period?.startDate === submission.period?.startDate &&
                other.period?.endDate === submission.period?.endDate &&
                new Date(other.timestamp) > new Date(submission.timestamp)
              );
              return !hasNewerSubmission; // Hide if there's a newer submission
            }
            return true; // Keep non-approved submissions
          });
        }
        
        // newest first
        list.sort((a,b)=> new Date(b.timestamp) - new Date(a.timestamp));
        setSubmissions(list);
      }
    } catch (e) {
      console.error('❌ Load submissions failed', e);
    } finally {
      setLoadingSubs(false);
    }
  };

  const samePeriod = (a, b) => {
    if (!a || !b) return false;
    return a.startDate === b.startDate && a.endDate === b.endDate;
  };

  const loadMyStatus = async () => {
    try {
      if (!selectedProtocol || !universePeriod) { setMyStatus(null); return; }
      const res = await fetch(`${SCRUBBER_API_BASE}/api/submissions?me=true`);
      const data = await res.json();
      if (!data?.success) return;
      const list = (data.submissions || [])
        .filter(s => s.protocol === selectedProtocol && samePeriod(s.period, universePeriod))
        .sort((a,b)=> new Date(b.timestamp) - new Date(a.timestamp));
      setMyStatus(list[0] || null);
    } catch (e) {
      console.error('❌ Load my status failed', e);
      setMyStatus(null);
    }
  };

  const actOnSubmission = async (id, action) => {
    try {
      const body = { action };
      if (action === 'reject') {
        body.reason = reviewReason[id] || 'Other';
        body.notes = reviewNotes[id] || '';
        if (!body.notes) {
          alert('Please provide notes for changes requested.');
          return;
        }
      }
      const res = await csrfFetch(`${SCRUBBER_API_BASE}/api/submissions/${id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Review failed');
      }
      await loadSubmissions();
    } catch (e) {
      console.error('❌ Review failed', e);
      alert('Review failed');
    }
  };

  const handleProtocolSelect = (protocol) => {
    setSelectedProtocol(protocol);
    setSelectedTable(null);
    setUniversePeriod(null);
    setUploadedFile(null);
    setValidationResult(null);
    setMyStatus(null);
  };

  const handleNavigation = (viewId) => {
    setActiveView(viewId);
    // Reset all states when switching views
    setSelectedProtocol(null);
    setSelectedTable(null);
    setShowPeriodModal(false);
    setUniversePeriod(null);
    setUploadedFile(null);
    setValidationResult(null);
  };

  const handleTableSelect = (table) => {
    setSelectedTable(table);
    // Show period modal for ODAG, CDAG, and FA; CPE and SNPCC use engagement date input
    if (selectedProtocol === 'ODAG' || selectedProtocol === 'CDAG' || selectedProtocol === 'FA') {
    setShowPeriodModal(true);
    }
  };

  // Handle resubmission for department owners
  const handleResubmit = (submission) => {
    // Navigate back to the main upload flow to create a new submission
    setActiveView('upload');
    setSelectedProtocol(submission.protocol);
    // Clear any existing data to start fresh
    setValidationResult(null);
    setUploadedFile(null);
    setSelectedTable(null);
    setUniversePeriod(null);
    alert(`Resubmitting ${submission.protocol} universe. Please upload your corrected file.`);
  };

  // Handle individual row resubmission
  const handleIndividualResubmit = (submission) => {
    const confirmResubmit = window.confirm(
      `Are you sure you want to resubmit this ${submission.protocol} universe?\n\n` +
      `This will:\n` +
      `• Navigate you to the upload interface\n` +
      `• Pre-select ${submission.protocol} protocol\n` +
      `• Allow you to upload a corrected file\n` +
      `• Update this existing submission (no new row)\n\n` +
      `Click OK to proceed with resubmission.`
    );

    if (confirmResubmit) {
      // Store the original submission info for resubmission (not new submission)
      sessionStorage.setItem('resubmissionContext', JSON.stringify({
        mode: 'resubmit', // Flag to indicate this is a resubmission
        originalId: submission.id,
        protocol: submission.protocol,
        tableLabel: submission.tableLabel,
        errorCount: submission.errorCount,
        period: submission.period,
        resubmissionReason: 'User-initiated resubmission for error correction'
      }));

      // Navigate to upload interface
      setActiveView('upload');
      setSelectedProtocol(submission.protocol);
      
      // Clear any existing data to start fresh
      setValidationResult(null);
      setUploadedFile(null);
      setSelectedTable(null);
      setUniversePeriod(null);

      // Show helpful message
      alert(`✅ Ready to resubmit ${submission.protocol} universe.\n\nPlease upload your corrected file to create a new submission.`);
    }
  };

  const viewerOnly = Boolean(validationResult?.universe);

  // Refresh owner status when protocol/period changes (not on review screen)
  React.useEffect(() => {
    if (activeView !== 'submissions') loadMyStatus();
    if (activeView === 'submissions') loadSubmissions();
    // Load notifications
    loadNotifications();
  }, [activeView, selectedProtocol, universePeriod, user]);

  // Refresh notifications every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <div className="min-h-screen text-slate-900 selection:bg-rose-600 selection:text-white
                    bg-[#f8fafc] flex relative overflow-hidden">

      {/* Platform-wide rail — the same 64px rail that Core renders, lets the
          user jump back to Core / POM or sideways to any other module.
          Hidden in embedded mode (rendered inside Core's iframe shell). */}
      {!isEmbedded && <PlatformRail user={user} />}

      {/* Sidebar — also hidden when embedded inside Core. */}
      {!isEmbedded && (
        <ScrubberSidebar
          user={user}
          onNavigate={handleNavigation}
          activeView={activeView}
          notificationCount={unreadCount}
        />
      )}

      {/* Main Content Area. Offset only when our own chrome is rendered;
          in embedded mode we sit flush against Core's shell. */}
      <div className={`flex-1 flex flex-col relative z-10 ${isEmbedded ? '' : 'ml-[136px]'}`}>
        <PendingUniverseRequestBanner />
        {/* Top bar */}
        <div className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
          <div className="mx-auto max-w-screen-2xl px-4 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="relative">
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 text-white grid place-items-center text-xs font-bold shadow-lg shadow-rose-500/25">
                  S
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-white shadow shadow-emerald-400/50"></div>
              </div>
              <span className="hidden sm:inline text-sm font-bold text-slate-800 tracking-tight">
                Precision Scrubber Tool
              </span>
            </div>

            {viewerOnly ? (
              <div className="flex flex-wrap items-center gap-2 justify-end flex-1 min-w-0">
                {/* Info badges — hide on small screens */}
                {uploadedFile?.name && (
                  <div className="hidden lg:block truncate max-w-[24ch] px-2.5 py-1 bg-white rounded-lg border border-slate-200 text-xs" title={uploadedFile.name}>
                    <span className="text-slate-600 font-medium">{uploadedFile.name}</span>
                  </div>
                )}
                <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-rose-50 rounded-lg border border-rose-200 text-xs">
                  <CalendarIcon className="w-3.5 h-3.5 text-rose-500" />
                  <span className="text-rose-700 font-semibold">{universePeriod?.startDate} → {universePeriod?.endDate}</span>
                </div>
                <div className="px-2.5 py-1 bg-red-50 rounded-lg border border-red-200 text-xs">
                  <span className="font-bold text-red-600">{(validationResult?.errors?.length ?? 0)} issues</span>
                </div>

                {/* Status badge */}
                {myStatus && (
                  <span className={`${myStatus.status==='approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : myStatus.status==='changes_requested' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-700 border-slate-200'} hidden sm:inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border` }>
                    {myStatus.status.replace('_',' ')} • v{myStatus.version}
                  </span>
                )}

                {/* Action buttons */}
                {selectedProtocol === 'SNPCC' && validationResult?.hratIAEligibleCount > 0 && !hratIAPreview && (
                  <button
                    onClick={handleGenerateHratIA}
                    disabled={isGeneratingHratIA}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 shadow-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingHratIA ? (
                      <>
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="hidden sm:inline">Generating...</span>
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">HRAT-IA ({validationResult.hratIAEligibleCount})</span>
                      </>
                    )}
                  </button>
                )}

                <select
                  value={entityLabel}
                  onChange={(e) => setEntityLabel(e.target.value)}
                  title="Whose universe is this? 'Internal' or an FDR from the FDR Oversight module — this label is how compliance identifies the file when scoring the oversight calendar."
                  className="w-44 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-rose-400"
                >
                  <option value="" disabled>— Whose universe is this? —</option>
                  <option value="Internal">Internal</option>
                  {entityOptions.map((e) => (
                    <option key={e.id} value={e.name}>{e.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleSubmitFinal}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold bg-gradient-to-r from-rose-500 to-orange-600 hover:opacity-90 shadow-sm shadow-rose-500/25 focus:outline-none"
                >
                  Submit
                </button>
                <button
                  onClick={() => handleExport('xlsx')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 shadow-sm focus:outline-none"
                >
                  Excel
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-slate-700 text-xs font-semibold bg-slate-100 hover:bg-slate-200 border border-slate-200 focus:outline-none"
                >
                  CSV
                </button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-4">
                <div className="flex items-center gap-3 px-5 py-2.5 bg-white/90 backdrop-blur-2xl rounded-2xl border border-slate-200/60 shadow-lg">
                  <span className="text-sm font-bold text-slate-800">Audit</span>
                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-rose-400 to-orange-500 rounded-full animate-pulse shadow-lg shadow-rose-500/50"></div>
                  <span className="text-sm font-bold text-slate-800">Validate</span>
                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-orange-400 to-amber-500 rounded-full animate-pulse shadow-lg shadow-orange-500/50"></div>
                  <span className="text-sm font-bold text-slate-800">Export</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* DEBUG: Role Switcher (temporary for testing) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-red-100 border-b border-red-200 px-6 py-2">
            <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
              <span className="text-red-800 text-sm font-medium">
                DEBUG MODE - Current Role: {isComplianceAdmin() ? 'Compliance Admin' : 'Department Owner'}
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    // Simulate compliance admin user
                    const complianceUser = { role: 'admin', email: 'compliance@test.com', name: 'Compliance Admin' };
                    console.log('🔄 Switching to compliance admin');
                    // This is just for UI testing - doesn't actually change the user
                  }}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded"
                >
                  Test as Compliance
                </button>
                <button 
                  onClick={() => {
                    // Simulate department owner
                    const deptUser = { role: 'user', email: 'dept@test.com', name: 'Department Owner' };
                    console.log('🔄 Switching to department owner');
                    // This is just for UI testing - doesn't actually change the user
                  }}
                  className="px-2 py-1 bg-orange-600 text-white text-xs rounded"
                >
                  Test as Department
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Banner */}
        {unreadCount > 0 && (
          <div className="bg-gradient-to-r from-rose-50 to-orange-50 border-b border-rose-200/60 px-6 py-3">
            <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse"></div>
                <span className="text-rose-800 font-medium">
                  🔔 You have {unreadCount} new notification{unreadCount !== 1 ? 's' : ''}
                </span>
              </div>
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="text-rose-600 hover:text-rose-800 font-medium text-sm"
              >
                {showNotifications ? 'Hide' : 'View'}
              </button>
            </div>
          </div>
        )}

        {/* Notification Details */}
        {showNotifications && (
          <div className="bg-white border-b border-slate-200 px-6 py-4">
            <div className="max-w-screen-2xl mx-auto">
              <h3 className="font-semibold text-slate-800 mb-3">Recent Notifications</h3>
              <div className="space-y-2">
                {notifications.slice(0, 5).map(notif => (
                  <div key={notif.id} className={`p-3 rounded-lg border ${notif.read ? 'bg-slate-50 border-slate-200' : 'bg-rose-50 border-rose-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm ${notif.read ? 'text-slate-600' : 'text-rose-800 font-medium'}`}>
                          {notif.message}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(notif.timestamp).toLocaleString()}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-rose-500 rounded-full ml-3 mt-1"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      {/* Full-bleed Universe table OR Compliance Review list OR FDR / Aggregator modules */}
      {activeView === 'fdr-invitations' ? (
        <main className="flex-1"><FDRInvitationsList /></main>
      ) : activeView === 'fdr-submissions' ? (
        <main className="flex-1"><FDRSubmissionsViewer /></main>
      ) : activeView === 'aggregator' ? (
        <main className="flex-1"><AggregatorPanel /></main>
      ) : activeView === 'submissions' ? (
        <main className="flex-1 p-8">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black tracking-tight bg-gradient-to-r from-slate-900 via-rose-800 to-orange-700 bg-clip-text text-transparent">Compliance Review</h2>
              <div className="flex gap-2">
                <button 
                  onClick={handleComplianceExport} 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-sm focus:outline-none"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Excel
                </button>
                <button onClick={loadSubmissions} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-sm">Refresh</button>
              </div>
            </div>

            {/* Review Period Filters */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Review Period & Filters</h3>
                <div className="flex gap-2">
                  <button onClick={() => setQuickDateRange('today')} className="px-3 py-1 text-xs bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200">Today</button>
                  <button onClick={() => setQuickDateRange('week')} className="px-3 py-1 text-xs bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200">Last 7 Days</button>
                  <button onClick={() => setQuickDateRange('month')} className="px-3 py-1 text-xs bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200">Last 30 Days</button>
                  <button onClick={() => setQuickDateRange('clear')} className="px-3 py-1 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">Clear All</button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Date From */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={reviewDateFrom}
                    onChange={(e) => setReviewDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={reviewDateTo}
                    onChange={(e) => setReviewDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                  <select
                    value={reviewStatusFilter}
                    onChange={(e) => setReviewStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    <option value="all">All Statuses</option>
                    <option value="submitted">Submitted</option>
                    <option value="changes_requested">Changes Requested</option>
                    <option value="resubmitted">Resubmitted</option>
                    <option value="approved">Approved</option>
                  </select>
                </div>

                {/* Protocol Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Protocol</label>
                  <select
                    value={reviewProtocolFilter}
                    onChange={(e) => setReviewProtocolFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    <option value="all">All Protocols</option>
                    <option value="ODAG">ODAG</option>
                    <option value="CDAG">CDAG</option>
                    <option value="SNPCC">SNPCC</option>
                    <option value="CPE">CPE</option>
                    <option value="FA">FA</option>
                  </select>
                </div>
              </div>

              {/* Results Summary */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>Showing {getFilteredSubmissions().length} of {submissions.length} submissions</span>
                  {(reviewDateFrom || reviewDateTo || reviewStatusFilter !== 'all' || reviewProtocolFilter !== 'all') && (
                    <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-lg text-xs">Filters Active</span>
                  )}
                </div>
              </div>
            </div>
            {loadingSubs ? (
              <div className="text-slate-600">Loading…</div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow">
                <table className="w-full text-left">
                  <thead className="text-slate-700">
                    <tr>
                      <th className="py-3 px-4 border-b">When</th>
                      <th className="py-3 px-4 border-b">Protocol</th>
                      <th className="py-3 px-4 border-b">Table</th>
                      <th className="py-3 px-4 border-b">Universe Period</th>
                      <th className="py-3 px-4 border-b">Rows</th>
                      <th className="py-3 px-4 border-b">Errors</th>
                      <th className="py-3 px-4 border-b">Error Details</th>
                      <th className="py-3 px-4 border-b">Status</th>
                      <th className="py-3 px-4 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    {getFilteredSubmissions().map(s => (
                      <React.Fragment key={s.id}>
                        <tr className="align-top">
                          <td className="py-3 px-4 border-b text-sm">{new Date(s.timestamp).toLocaleString()}</td>
                          <td className="py-3 px-4 border-b">{s.protocol}</td>
                          <td className="py-3 px-4 border-b">{s.tableLabel}</td>
                          <td className="py-3 px-4 border-b text-sm">
                            {s.period && s.period.startDate && s.period.endDate ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-700">
                                  {s.period.startDate} - {s.period.endDate}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {Math.ceil((new Date(s.period.endDate) - new Date(s.period.startDate)) / (1000 * 60 * 60 * 24))} days
                                </span>
                              </div>
                            ) : s.engagementDate ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-700">
                                  {s.engagementDate}
                                </span>
                                <span className="text-xs text-slate-500">
                                  Engagement Date
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">Not specified</span>
                            )}
                          </td>
                          <td className="py-3 px-4 border-b">{s.universeRowCount}</td>
                          <td className="py-3 px-4 border-b">{s.errorCount}</td>
                          <td className="py-3 px-4 border-b">
                            {s.errorCount > 0 ? (
                              <div className="flex flex-col gap-2">
                                <button 
                                  onClick={() => setShowErrorDetails(prev => ({...prev, [s.id]: !prev[s.id]}))}
                                  className="text-rose-600 hover:text-rose-800 text-sm underline"
                                >
                                  {showErrorDetails[s.id] ? 'Hide' : 'View'} Details
                                </button>
                                <button 
                                  onClick={() => handleIndividualExport(s)}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-white text-xs font-medium bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-sm focus:outline-none"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Export Errors
                                </button>
                              </div>
                            ) : (
                              <span className="text-green-600 text-sm">No errors</span>
                            )}
                          </td>
                          <td className="py-3 px-4 border-b">
                            <div className="capitalize">
                              {s.status.replace('_',' ')}
                              {/* Show compliance notes if status is changes_requested */}
                              {s.status === 'changes_requested' && s.complianceNotes && (
                                <div className="mt-1 text-xs text-slate-600 bg-yellow-50 p-2 rounded border">
                                  <strong>Notes:</strong> {s.complianceNotes}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 border-b">
                            {/* Compliance Admin Actions */}
                            {isComplianceAdmin() ? (
                              <div className="flex flex-col gap-2">
                                <div className="flex gap-2">
                                  <button onClick={()=>actOnSubmission(s.id, 'accept')} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm">Approve</button>
                                  <button onClick={()=>actOnSubmission(s.id, 'reject')} className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-sm">Request Changes</button>
                                </div>
                                <select className="border border-slate-200 rounded-md px-2 py-1 text-sm" value={reviewReason[s.id]||''} onChange={(e)=>setReviewReason(r=>({...r,[s.id]:e.target.value}))}>
                                  <option value="">Select reason…</option>
                                  <option value="Timeliness">Timeliness</option>
                                  <option value="Classification">Classification</option>
                                  <option value="Eligibility">Eligibility</option>
                                  <option value="Documentation">Documentation</option>
                                  <option value="Data Quality">Data Quality</option>
                                  <option value="Other">Other</option>
                                </select>
                                <textarea className="border border-slate-200 rounded-md px-2 py-1 text-sm" placeholder="Notes (required for Request Changes)" rows={2} value={reviewNotes[s.id]||''} onChange={(e)=>setReviewNotes(n=>({...n,[s.id]:e.target.value}))} />
                              </div>
                            ) : (
                              /* Department Owner Actions */
                              <div className="text-center">
                                {s.submittedBy === getUserEmail() ? (
                                  <div className="flex flex-col gap-2">
                                    {/* Status Display */}
                                    <span className={`text-sm font-medium ${
                                      s.status === 'submitted' ? 'text-orange-600' :
                                      s.status === 'approved' ? 'text-green-600' :
                                      s.status === 'changes_requested' ? 'text-orange-600' :
                                      'text-slate-500'
                                    }`}>
                                      {s.status === 'submitted' ? 'Pending Review' :
                                       s.status === 'approved' ? 'Approved ✓' :
                                       s.status === 'changes_requested' ? 'Changes Requested' :
                                       s.status}
                                    </span>
                                    
                                    {/* Resubmit Button - Show for non-approved submissions */}
                                    {s.status !== 'approved' && (
                                      <button 
                                        onClick={() => handleIndividualResubmit(s)}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-white text-xs font-medium bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-sm focus:outline-none"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Resubmit
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  /* Other users' submissions - just show status */
                                  <span className="text-slate-500 text-sm">
                                    {s.status === 'submitted' ? 'Pending Review' :
                                     s.status === 'approved' ? 'Approved ✓' :
                                     s.status === 'changes_requested' ? 'Changes Requested' :
                                     s.status}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        {showErrorDetails[s.id] && s.errorDetails && s.errorDetails.length > 0 && (
                          <tr>
                            <td colSpan={9} className="py-4 px-4 border-b bg-slate-50">
                              <div className="max-h-64 overflow-y-auto">
                                <h4 className="font-medium text-slate-800 mb-2">Validation Errors (No PHI/PII)</h4>
                                <div className="space-y-1">
                                  {s.errorDetails.map((error, idx) => (
                                    <div key={idx} className="text-sm p-2 bg-white rounded border-l-4 border-red-400">
                                      <span className="font-medium text-slate-700">
                                        Row {error.row}, Field "{error.field}":
                                      </span>
                                      <span className="text-slate-600 ml-2">{error.message}</span>
                                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                        error.category === 'Timeliness' ? 'bg-orange-100 text-orange-700' :
                                        error.category === 'Classification' ? 'bg-purple-100 text-purple-700' :
                                        error.category === 'Data Quality' ? 'bg-red-100 text-red-700' :
                                        error.category === 'Missing Info' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {error.category}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {getFilteredSubmissions().length === 0 && (
                      <tr>
                        <td className="py-6 px-4 text-slate-500 text-center" colSpan={9}>
                          {submissions.length === 0 ? 'No submissions found.' : 'No submissions match the selected filters.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      ) : viewerOnly ? (
        <main className="w-full max-w-none">
          <div className="h-[calc(100vh-80px)] w-full">
            {/* SNPCC with HRAT-IA Preview */}
            {selectedProtocol === 'SNPCC' && hratIAPreview ? (
              <div className="h-full overflow-y-auto p-6 space-y-8">
                {/* Universe Viewer */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800">SNPE Universe Validation Results</h2>
                  </div>
                  <div className="h-96 overflow-hidden">
            <UniverseViewer
              universe={validationResult.universe}
              errors={validationResult.errors}
              headerCorrections={headerCorrections}
              setHeaderCorrections={setHeaderCorrections}
              selectedTable={selectedTable}
              userEmail={getUserEmail()}
              submissionId={validationResult.submissionId || validationResult.submission_id}
              timelinessSummary={validationResult.timelinessSummary}
              monthlyTimeliness={validationResult.monthlyTimeliness}
              timeliness={validationResult.timeliness}
              totalRows={validationResult.totalRows}
              universeMode={validationResult.universeMode}
              serverTotalErrorCount={validationResult.totalErrorCount}
              cleanRowsOmitted={validationResult.universeCleanRowsOmitted}
            />
                  </div>
                </div>

                {/* HRAT-IA Preview */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800">HRAT-IA (Table 2IA) Preview</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Review the auto-generated Impact Analysis table before final submission
                    </p>
                  </div>
                  <div className="p-6">
                    <HRATIAViewer 
                      hratIA={hratIAPreview}
                      onExport={handleExport}
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Standard Universe Viewer for other protocols or SNPCC without preview */
              <UniverseViewer
                universe={validationResult.universe}
                errors={validationResult.errors}
                headerCorrections={headerCorrections}
                setHeaderCorrections={setHeaderCorrections}
                selectedTable={selectedTable}
                userEmail={getUserEmail()}
                submissionId={validationResult.submissionId || validationResult.submission_id}
                timelinessSummary={validationResult.timelinessSummary}
              monthlyTimeliness={validationResult.monthlyTimeliness}
                timeliness={validationResult.timeliness}
                totalRows={validationResult.totalRows}
                universeMode={validationResult.universeMode}
              serverTotalErrorCount={validationResult.totalErrorCount}
                cleanRowsOmitted={validationResult.universeCleanRowsOmitted}
              timeliness={validationResult.timeliness}
              totalRows={validationResult.totalRows}
              universeMode={validationResult.universeMode}
              serverTotalErrorCount={validationResult.totalErrorCount}
              cleanRowsOmitted={validationResult.universeCleanRowsOmitted}
              />
            )}
          </div>
        </main>
      ) : (
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-8xl mx-auto">
            {/* Ultra-modern centered workflow section */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/90 backdrop-blur-2xl border border-slate-200/60 rounded-2xl shadow-2xl mb-8">
                <div className="w-3 h-3 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 rounded-full animate-pulse shadow-lg shadow-rose-500/50"></div>
                <span className="text-sm font-bold text-slate-700 tracking-wider uppercase">Workflow</span>
              </div>
              
              <h1 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-rose-800 to-orange-700 mb-6 tracking-tight">
                {!selectedProtocol ? 'Select Protocol' : 
                 (selectedProtocol === 'ODAG' || selectedProtocol === 'CDAG' || selectedProtocol === 'CPE' || selectedProtocol === 'FA') && !selectedTable ? 'Choose Universe Table' :
                 (selectedProtocol === 'ODAG' || selectedProtocol === 'CDAG' || selectedProtocol === 'FA') && !universePeriod ? 'Set Universe Period' :
                 !uploadedFile ? 'Upload Data File' : 'Analysis Complete'}
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-light">
                {!selectedProtocol ? 'Choose the compliance protocol that matches your audit requirements' :
                 (selectedProtocol === 'ODAG' || selectedProtocol === 'CPE' || selectedProtocol === 'SNPCC') && !selectedTable ? 'Select the specific universe table for your analysis' :
                 selectedProtocol === 'ODAG' && !universePeriod ? 'Define the time period for your universe data' :
                 !uploadedFile ? (
                   selectedProtocol === 'CPE' ? 'Upload your CPE COA file with engagement date for validation' :
                   selectedProtocol === 'SNPCC' ? 'Upload your SNPE file with engagement date for HRA timeliness validation' :
                   'Upload your data file for validation and analysis'
                 ) : 'Review your analysis results and export findings'}
              </p>
            </div>

            {/* Ultra-modern workflow container */}
            <div className="max-w-7xl mx-auto">
              {!selectedProtocol ? (
                <div className="bg-white/90 backdrop-blur-2xl rounded-3xl p-10 border border-slate-200/60 shadow-2xl shadow-slate-200/50">
                  <ProtocolPicker 
                    onSelect={handleProtocolSelect} 
                    userScopes={user?.app_roles?.scrubber?.scopes || []}
                  />
                </div>
              ) : (selectedProtocol === 'ODAG' || selectedProtocol === 'CDAG' || selectedProtocol === 'CPE' || selectedProtocol === 'SNPCC' || selectedProtocol === 'FA') && !selectedTable ? (
                <div className="bg-white/90 backdrop-blur-2xl rounded-3xl p-10 border border-slate-200/60 shadow-2xl shadow-slate-200/50">
                  <div className="mb-8 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`px-6 py-3 ${
                        selectedProtocol === 'ODAG' ? 'bg-orange-50/90 border-orange-200/60' :
                        selectedProtocol === 'CDAG' ? 'bg-amber-50/90 border-amber-200/60' :
                        selectedProtocol === 'CPE' ? 'bg-amber-50/90 border-amber-200/60' :
                        selectedProtocol === 'FA' ? 'bg-pink-50/90 border-pink-200/60' :
                        'bg-emerald-50/90 border-emerald-200/60'
                      } backdrop-blur-xl rounded-2xl border shadow-lg`}>
                        <span className={`text-base font-bold ${
                          selectedProtocol === 'ODAG' ? 'text-orange-700' :
                          selectedProtocol === 'CDAG' ? 'text-amber-700' :
                          selectedProtocol === 'CPE' ? 'text-amber-700' :
                          selectedProtocol === 'FA' ? 'text-pink-700' :
                          'text-emerald-700'
                        }`}>Selected: {selectedProtocol}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedProtocol(null)}
                      className="px-6 py-3 bg-slate-100/90 hover:bg-slate-200/90 backdrop-blur-xl rounded-2xl border border-slate-200/60 transition-all duration-300 text-sm font-semibold text-slate-700 shadow-lg"
                    >
                      ← Change Protocol
                    </button>
                  </div>
                  {selectedProtocol === 'ODAG' ? (
                  <ODAGTableSelector onSelect={handleTableSelect} />
                  ) : selectedProtocol === 'CDAG' ? (
                    <CDAGTableSelector onSelect={handleTableSelect} />
                  ) : selectedProtocol === 'CPE' ? (
                    <CPETableSelector onSelect={handleTableSelect} />
                  ) : selectedProtocol === 'FA' ? (
                    <FATableSelector onSelect={handleTableSelect} />
                  ) : (
                    <SNPCCTableSelector onSelect={handleTableSelect} />
                  )}
                </div>
              ) : showPeriodModal ? (
                <div className="bg-white/90 backdrop-blur-2xl rounded-3xl border border-slate-200/60 shadow-2xl shadow-slate-200/50 p-8">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/90 backdrop-blur-2xl border border-slate-200/60 rounded-2xl shadow-2xl mb-8">
                      <div className="w-3 h-3 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 rounded-full animate-pulse shadow-lg shadow-rose-500/50"></div>
                      <span className="text-sm font-bold text-slate-700 tracking-wider uppercase">Universe Period</span>
                    </div>
                    <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-rose-800 to-orange-700 mb-4 tracking-tight">
                      Set Universe Period
                    </h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed font-light">
                      Define the time period for your universe data
                    </p>
                  </div>
                </div>
              ) : (selectedProtocol === 'ODAG' || selectedProtocol === 'CDAG' || selectedProtocol === 'FA') && !universePeriod ? (
                <div className="text-center p-12 bg-red-50/90 backdrop-blur-xl rounded-3xl border border-red-200/60 shadow-2xl">
                  <div className="text-red-600 font-bold text-xl">⚠️ Universe period not captured.</div>
                </div>
              ) : !uploadedFile ? (
                <div className="bg-white/90 backdrop-blur-2xl rounded-3xl p-10 border border-slate-200/60 shadow-2xl shadow-slate-200/50">
                  <div className="mb-8 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`px-6 py-3 ${
                        selectedProtocol === 'ODAG' ? 'bg-orange-50/90 border-orange-200/60' :
                        selectedProtocol === 'CDAG' ? 'bg-amber-50/90 border-amber-200/60' :
                        selectedProtocol === 'CPE' ? 'bg-amber-50/90 border-amber-200/60' :
                        selectedProtocol === 'FA' ? 'bg-pink-50/90 border-pink-200/60' :
                        'bg-emerald-50/90 border-emerald-200/60'
                      } backdrop-blur-xl rounded-2xl border shadow-lg`}>
                        <span className={`text-base font-bold ${
                          selectedProtocol === 'ODAG' ? 'text-orange-700' :
                          selectedProtocol === 'CDAG' ? 'text-amber-700' :
                          selectedProtocol === 'CPE' ? 'text-amber-700' :
                          selectedProtocol === 'FA' ? 'text-pink-700' :
                          'text-emerald-700'
                        }`}>{selectedProtocol} • {selectedTable?.label || 'Table'}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTable(null);
                        setShowPeriodModal(false);
                      }}
                      className="px-6 py-3 bg-slate-100/90 hover:bg-slate-200/90 backdrop-blur-xl rounded-2xl border border-slate-200/60 transition-all duration-300 text-sm font-semibold text-slate-700 shadow-lg"
                    >
                      ← Change Table
                    </button>
                  </div>
                  {(selectedProtocol === 'ODAG' || selectedProtocol === 'CDAG' || selectedProtocol === 'FA') ? (
                  <FileUploadPanel
                    onFileUpload={setUploadedFile}
                    onValidationComplete={setValidationResult}
                    selectedTable={selectedTable}
                    headerCorrections={headerCorrections}
                  />
                  ) : selectedProtocol === 'CPE' ? (
                    <CPEFileUploadPanel
                      onFileUpload={setUploadedFile}
                      onValidationComplete={setValidationResult}
                      selectedTable={selectedTable}
                      onUniversePeriodChange={setUniversePeriod}
                    />
                  ) : (
                    <SNPCCFileUploadPanel
                      onFileUpload={setUploadedFile}
                      onValidationComplete={setValidationResult}
                      selectedTable={selectedTable}
                      onEngagementDateChange={setEngagementDate}
                    />
                  )}
                </div>
              ) : (
                <div className="bg-white/90 backdrop-blur-2xl rounded-3xl border border-slate-200/60 shadow-2xl shadow-slate-200/50 overflow-hidden">
                  <header className="px-10 py-8 border-b border-slate-200/60 bg-gradient-to-r from-slate-50/90 via-white to-rose-50/90">
                    <div className="flex items-center gap-4">
                      <span className="h-4 w-4 rounded-full bg-gradient-to-r from-rose-500 to-orange-600 shadow-[0_0_0_6px_rgba(244,63,94,0.12)]" />
                      <span className="text-2xl font-black tracking-tight text-slate-900">Analysis Summary</span>
                    </div>
                  </header>
                  <div className="p-10">
                    <SummaryCard
                      uploadedFile={uploadedFile}
                      selectedTable={selectedTable}
                      universePeriod={universePeriod}
                      validationResult={validationResult}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Bottom spacing */}
            <div className="h-16"></div>
          </div>
        </main>
      )}

      {/* Universe Period Modal - Rendered at root level for proper centering */}
      {showPeriodModal && (
        <UniversePeriodModal
          selectedTable={selectedTable}
          tableHint={selectedTable?.hint}
          onClose={() => {
            setSelectedTable(null);
            setShowPeriodModal(false);
          }}
          onSubmit={(period) => {
            setUniversePeriod(period);
            setShowPeriodModal(false);
          }}
        />
      )}

      {/* Submit as Final — scrubbed here, then filed to its destination:
          monitoring (compliance review) or an open internal audit in Core. */}
      <SubmitFinalDialog
        open={showSubmitDialog}
        onClose={() => setShowSubmitDialog(false)}
        totalIssues={validationResult?.errors?.length || 0}
        universe={validationResult?.universe || []}
        uploadedFile={uploadedFile}
        totalRows={validationResult?.totalRows || 0}
        errors={validationResult?.errors || []}
        timeliness={validationResult?.timeliness || null}
        timelinessSummary={validationResult?.timelinessSummary || null}
        selectedTable={selectedTable}
        universePeriod={universePeriod || (selectedProtocol === 'SNPCC' ? snpccDeclaredPeriod() : null)}
        onSubmitMonitoring={submitMonitoringFinal}
      />
        </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

// Wrap the app with SessionProvider, unless the request targets the FDR
// submission portal — that is a standalone, no-SSO page and must not
// attempt a Core session check.
export default function App() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const fdrPortalMatch = path.match(/^\/fdr\/portal\/([^/?#]+)/);
  if (fdrPortalMatch) {
    return <FDRPortal token={decodeURIComponent(fdrPortalMatch[1])} />;
  }
  return (
    <SessionProvider>
      <AppContent />
    </SessionProvider>
  );
}
